import React, { useState, useEffect, useCallback } from "react";
import PageWrapper from "../../components/layout/PageWrapper";
import useAuth from "../../hooks/useAuth";
import useToast from "../../hooks/useToast";
import { useAttendanceSocket } from "../../hooks/useAttendanceSocket";
import { attendanceService } from "../../api/attendanceService";
import {
  Clock,
  LogIn,
  LogOut,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Briefcase,
  MapPin,
  Wifi,
  WifiOff,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
} from "lucide-react";

const STATUS_CONFIG = {
  Working: {
    color: "text-blue-600 dark:text-blue-400",
    bg: "bg-blue-100 dark:bg-blue-900/30",
    icon: Briefcase,
  },
  Present: {
    color: "text-green-600 dark:text-green-400",
    bg: "bg-green-100 dark:bg-green-900/30",
    icon: CheckCircle2,
  },
  WFH: {
    color: "text-purple-600 dark:text-purple-400",
    bg: "bg-purple-100 dark:bg-purple-900/30",
    icon: MapPin,
  },
  "No Check-out": {
    color: "text-amber-600 dark:text-amber-400",
    bg: "bg-amber-100 dark:bg-amber-900/30",
    icon: AlertCircle,
  },
  Absent: {
    color: "text-red-600 dark:text-red-400",
    bg: "bg-red-100 dark:bg-red-900/30",
    icon: XCircle,
  },
  Inactive: {
    color: "text-orange-600 dark:text-orange-400",
    bg: "bg-orange-100 dark:bg-orange-900/30",
    icon: WifiOff,
  },
};

const formatTime = (dateStr) => {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  return d.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  const [year, month, day] = dateStr.split("-");
  const d = new Date(year, month - 1, day);
  return d.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const calcHours = (checkIn, checkOut) => {
  if (!checkIn) return "—";
  const start = new Date(checkIn);
  const end = checkOut ? new Date(checkOut) : new Date();
  const diffMs = end - start;
  const hours = Math.floor(diffMs / 3600000);
  const mins = Math.floor((diffMs % 3600000) / 60000);
  return `${hours}h ${mins}m`;
};

const TEN_MINUTES = 10 * 60 * 1000;

const getDisplayStatus = (rec) => {
  if (rec.status === "Working" && !rec.check_out) {
    if (
      !rec.last_seen ||
      Date.now() - new Date(rec.last_seen).getTime() > TEN_MINUTES
    ) {
      return "Inactive";
    }
  }
  return rec.status;
};

const MyAttendance = () => {
  const { auth } = useAuth();
  const { showToast } = useToast();
  const token =
    auth?.accessToken ||
    (typeof localStorage !== "undefined"
      ? localStorage.getItem("accessToken")
      : null);

  const [todayRecord, setTodayRecord] = useState(null);
  const [history, setHistory] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [checkingOut, setCheckingOut] = useState(false);

  // Socket: auto check-in + heartbeat
  const { isCheckedIn, isConnected, validationError, checkOut } =
    useAttendanceSocket({
      token,
      enabled: !!token,
      onCheckIn: (response) => {
        if (!response.alreadyCheckedIn) {
          showToast("Checked in successfully!", "success");
        }
        setTodayRecord(response.attendance);
      },
      onCheckOut: (response) => {
        showToast("Checked out successfully!", "success");
        setTodayRecord(response.attendance);
      },
      onError: (msg) => {
        // Only show errors that matter to the user
        if (msg && !msg.includes("heartbeat")) {
          showToast(msg, "error");
        }
      },
    });

  const fetchToday = useCallback(async () => {
    try {
      const data = await attendanceService.getToday();
      setTodayRecord(data.attendance);
    } catch {
      // silent
    }
  }, []);

  const fetchHistory = useCallback(async (p = 1) => {
    try {
      setLoading(true);
      const data = await attendanceService.getMyHistory(p, 20);
      setHistory(data.records || []);
      setTotalPages(data.totalPages || 1);
      setPage(data.page || 1);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchToday();
    fetchHistory(1);
  }, [fetchToday, fetchHistory]);

  const handleCheckOut = async () => {
    setCheckingOut(true);
    await checkOut();
    await Promise.all([fetchToday(), fetchHistory(1)]);
    setCheckingOut(false);
  };

  const todayStatus = todayRecord?.status;
  const statusCfg = STATUS_CONFIG[todayStatus] || STATUS_CONFIG["Working"];
  const StatusIcon = statusCfg?.icon || Clock;

  return (
    <PageWrapper title="Attendance" subtitle="Track your daily attendance">
      {/* Validation Error Banner */}
      {validationError && (
        <div className="mb-6 flex items-start gap-3 rounded-xl border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-4">
          <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-red-800 dark:text-red-300">
              Blocked
            </p>
            <p className="text-sm text-red-600 dark:text-red-400 mt-0.5">
              {validationError}
            </p>
          </div>
        </div>
      )}

      {/* Today's Status Card */}
      <div className="mb-8 grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Status Card */}
        <div className="bg-white dark:bg-primary-card rounded-xl border border-gray-200 dark:border-dark-100 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div
              className={`w-10 h-10 rounded-lg ${statusCfg.bg} flex items-center justify-center`}
            >
              <StatusIcon className={`w-5 h-5 ${statusCfg.color}`} />
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-light-500">
                Today's Status
              </p>
              <p className={`text-lg font-semibold ${statusCfg.color}`}>
                {todayRecord
                  ? todayStatus
                  : isCheckedIn
                    ? "Checking in..."
                    : "Not Checked In"}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 text-xs text-gray-400 dark:text-light-500">
            {isConnected ? (
              <>
                <Wifi className="w-3 h-3 text-green-500" />
                <span>Connected</span>
              </>
            ) : (
              <>
                <WifiOff className="w-3 h-3 text-red-500" />
                <span>Disconnected</span>
              </>
            )}
          </div>
        </div>

        {/* Check-in / Check-out Times */}
        <div className="bg-white dark:bg-primary-card rounded-xl border border-gray-200 dark:border-dark-100 p-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
              <LogIn className="w-5 h-5 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-light-500">
                Check In
              </p>
              <p className="text-lg font-semibold text-gray-900 dark:text-light-100">
                {formatTime(todayRecord?.check_in)}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
              <LogOut className="w-5 h-5 text-red-600 dark:text-red-400" />
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-light-500">
                Check Out
              </p>
              <p className="text-lg font-semibold text-gray-900 dark:text-light-100">
                {formatTime(todayRecord?.check_out)}
              </p>
            </div>
          </div>
        </div>

        {/* Hours & Checkout Button */}
        <div className="bg-white dark:bg-primary-card rounded-xl border border-gray-200 dark:border-dark-100 p-6 flex flex-col justify-between">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center">
              <Clock className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-light-500">
                Hours Worked
              </p>
              <p className="text-lg font-semibold text-gray-900 dark:text-light-100">
                {todayRecord
                  ? calcHours(todayRecord.check_in, todayRecord.check_out)
                  : "—"}
              </p>
            </div>
          </div>
          {todayRecord &&
            !todayRecord.check_out &&
            todayRecord.status === "Working" && (
              <button
                onClick={handleCheckOut}
                disabled={checkingOut}
                className="w-full py-2.5 px-4 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white rounded-lg font-medium text-sm transition-colors flex items-center justify-center gap-2"
              >
                <LogOut className="w-4 h-4" />
                {checkingOut ? "Checking Out..." : "Check Out"}
              </button>
            )}
        </div>
      </div>

      {/* History Table */}
      <div className="bg-white dark:bg-primary-card rounded-xl border border-gray-200 dark:border-dark-100 overflow-hidden shadow-sm">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-dark-100 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-light-100">
            Attendance History
          </h2>
          <button
            onClick={() => fetchHistory(page)}
            disabled={loading}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-600 dark:text-light-400 border border-gray-200 dark:border-dark-100 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-300 disabled:opacity-40 transition-colors"
          >
            <RefreshCw
              className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`}
            />
            Refresh
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-accent"></div>
          </div>
        ) : history.length === 0 ? (
          <div className="text-center py-16 text-gray-500 dark:text-light-500">
            No attendance records found
          </div>
        ) : (
          <>
            {/* Mobile Card View */}
            <div className="block md:hidden space-y-3">
              {history.map((rec) => {
                const displayStatus = getDisplayStatus(rec);
                const cfg =
                  STATUS_CONFIG[displayStatus] || STATUS_CONFIG["Working"];
                const RecIcon = cfg.icon;
                return (
                  <div
                    key={rec._id}
                    className="bg-white dark:bg-primary-card rounded-xl border border-gray-100 dark:border-dark-200 shadow-sm p-4"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm font-semibold text-gray-900 dark:text-light-100">
                        {formatDate(rec.date)}
                      </span>
                      <span
                        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${cfg.bg} ${cfg.color}`}
                      >
                        <RecIcon className="w-3 h-3" />
                        {displayStatus}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <p className="text-xs text-gray-500 dark:text-light-500 mb-0.5">
                          Check In
                        </p>
                        <p className="text-sm font-medium text-gray-900 dark:text-light-100">
                          {formatTime(rec.check_in) || "—"}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 dark:text-light-500 mb-0.5">
                          Check Out
                        </p>
                        <p className="text-sm font-medium text-gray-900 dark:text-light-100">
                          {formatTime(rec.check_out) || "—"}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 dark:text-light-500 mb-0.5">
                          Hours
                        </p>
                        <p className="text-sm font-medium text-gray-900 dark:text-light-100">
                          {calcHours(rec.check_in, rec.check_out)}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 dark:text-light-500 mb-0.5">
                          Last Seen
                        </p>
                        <p className="text-sm font-medium text-gray-900 dark:text-light-100">
                          {formatTime(rec.last_seen) || "—"}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Desktop Table View */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gradient-to-r from-gray-100 to-gray-50 dark:from-dark-400 dark:to-dark-300">
                    <th className="px-6 py-3 text-left text-xs font-bold text-gray-800 dark:text-light-200 uppercase tracking-wider border-b-2 border-primary-accent/20">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-bold text-gray-800 dark:text-light-200 uppercase tracking-wider border-b-2 border-primary-accent/20">
                      Check In
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-bold text-gray-800 dark:text-light-200 uppercase tracking-wider border-b-2 border-primary-accent/20">
                      Check Out
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-bold text-gray-800 dark:text-light-200 uppercase tracking-wider border-b-2 border-primary-accent/20">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-bold text-gray-800 dark:text-light-200 uppercase tracking-wider border-b-2 border-primary-accent/20">
                      Hours
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-bold text-gray-800 dark:text-light-200 uppercase tracking-wider border-b-2 border-primary-accent/20">
                      Last Seen
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-primary-card divide-y divide-gray-200 dark:divide-dark-100">
                  {history.map((rec) => {
                    const displayStatus = getDisplayStatus(rec);
                    const cfg =
                      STATUS_CONFIG[displayStatus] || STATUS_CONFIG["Working"];
                    const RecIcon = cfg.icon;
                    return (
                      <tr
                        key={rec._id}
                        className="hover:bg-gray-50 dark:hover:bg-dark-300 transition-colors"
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-light-100">
                          {formatDate(rec.date)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-light-300">
                          {formatTime(rec.check_in)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-light-300">
                          {formatTime(rec.check_out)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${cfg.bg} ${cfg.color}`}
                          >
                            <RecIcon className="w-3 h-3" />
                            {displayStatus}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-light-300">
                          {calcHours(rec.check_in, rec.check_out)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-light-300">
                          {formatTime(rec.last_seen)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="px-6 py-4 border-t border-gray-200 dark:border-dark-100 flex items-center justify-between">
                <p className="text-sm text-gray-500 dark:text-light-500">
                  Page {page} of {totalPages}
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => fetchHistory(page - 1)}
                    disabled={page <= 1}
                    className="p-2 rounded-lg border border-gray-200 dark:border-dark-100 text-gray-600 dark:text-light-400 hover:bg-gray-100 dark:hover:bg-dark-300 disabled:opacity-40 transition-colors"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => fetchHistory(page + 1)}
                    disabled={page >= totalPages}
                    className="p-2 rounded-lg border border-gray-200 dark:border-dark-100 text-gray-600 dark:text-light-400 hover:bg-gray-100 dark:hover:bg-dark-300 disabled:opacity-40 transition-colors"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </PageWrapper>
  );
};

export default MyAttendance;
